import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import { api } from '../lib/api';
import { useToast } from '../context/ToastContext';
import { formatarDataHora } from './imoveis/formatos';

// Diagnóstico dos apps do WhatsLight (extensão Chrome e app desktop) — só superadmin. Os apps informam versão e estado no pedido de
// mensagens que já fazem; o servidor grava no máximo uma vez por intervalo (configurável aqui). Nada de conteúdo de conversa.
const ESTADOS = {
  online: ['Online', 'success'],
  parado: ['Parado', 'warning'],
  offline: ['Offline', 'danger'],
  nunca: ['Nunca visto', 'secondary'],
};
const APPS = { chrome: 'Extensão Chrome', desktop: 'App desktop' };
const INTERVALOS = [
  [5, '5 minutos'],
  [15, '15 minutos'],
  [30, '30 minutos'],
  [60, '1 hora'],
  [120, '2 horas'],
  [360, '6 horas'],
];

function haQuanto(seg) {
  if (seg === null || seg === undefined) return '—';
  const min = Math.floor(seg / 60);
  if (min < 1) return 'agora';
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 48) return `há ${h} h`;
  return `há ${Math.floor(h / 24)} dias`;
}

// Barras simples (SVG, sem biblioteca): contas ativas em cada uma das últimas 48 horas
function GraficoHoras({ horas }) {
  const max = Math.max(1, ...horas.map((h) => h.contas));
  const largura = 100 / horas.length;
  return (
    <div>
      <svg viewBox="0 0 100 32" preserveAspectRatio="none" style={{ width: '100%', height: 110 }} role="img" aria-label="Contas ativas por hora nas últimas 48 horas">
        {horas.map((h, i) => {
          const alt = (h.contas / max) * 28;
          return (
            <rect key={h.hora} x={i * largura + 0.15} y={30 - alt} width={largura - 0.3} height={Math.max(alt, h.contas ? 0.8 : 0.3)} rx="0.3" fill={h.contas ? 'var(--cor-primaria, #0d6efd)' : '#ced4da'}>
              <title>{`${h.hora.slice(8, 10)}/${h.hora.slice(5, 7)} ${h.hora.slice(11, 13)}h: ${h.contas} conta(s)`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="d-flex justify-content-between small text-muted">
        <span>48 h atrás</span>
        <span>máx. {max} conta(s) numa hora</span>
        <span>agora</span>
      </div>
    </div>
  );
}

export default function AppsStatus() {
  const toast = useToast();
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [salvando, setSalvando] = useState(false);

  const carregar = useCallback(
    (primeira = false) =>
      api
        .get('/apps-status')
        .then((r) => {
          setDados(r);
          if (primeira) setCfg(r.config);
        })
        .catch((err) => toast.erro(err.message || 'Não foi possível carregar o diagnóstico'))
        .finally(() => setCarregando(false)),
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );
  useEffect(() => { carregar(true); }, [carregar]);
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) carregar(); }, 60000);
    return () => clearInterval(t);
  }, [carregar]);

  async function salvar() {
    setSalvando(true);
    try {
      const r = await api.put('/apps-status/config', cfg);
      setCfg(r.data);
      toast.sucesso('Configuração salva.');
      carregar();
    } catch (err) {
      toast.erro(err.message || 'Não foi possível salvar');
    } finally {
      setSalvando(false);
    }
  }

  if (carregando && !dados) return <Spinner />;
  if (!dados) return null;
  const { resumo, contas, grafico } = dados;
  const cartoes = [
    ['Contas ativas', resumo.ativas, 'secondary'],
    ['Online agora', resumo.online, 'success'],
    ['Desatualizadas', resumo.desatualizadas, resumo.desatualizadas ? 'warning' : 'secondary'],
    ['Abaixo da versão mínima', resumo.abaixoDoMinimo, resumo.abaixoDoMinimo ? 'danger' : 'secondary'],
    [`Sem acesso há ${dados.config.diasSemAcesso}+ dias`, resumo.semAcesso, resumo.semAcesso ? 'warning' : 'secondary'],
    ['WhatsApp desconectado', resumo.waDesconectado, resumo.waDesconectado ? 'danger' : 'secondary'],
  ];
  const mudou = cfg && JSON.stringify(cfg) !== JSON.stringify(dados.config);

  return (
    <div>
      <h1 className="h4 mb-2">Diagnóstico dos apps</h1>
      <Alert variant="info" className="py-2 small">
        Cada app informa a versão e o estado no mesmo pedido de mensagens que já faz; o servidor registra no máximo uma vez a cada{' '}
        <b>{INTERVALOS.find(([m]) => m === dados.config.intervaloMin)?.[1] || `${dados.config.intervaloMin} min`}</b> por conta. Apps antigos ainda não informam a versão
        (aparecem como "sem informação"), mas a última vez visto já é registrada. Nenhum conteúdo de conversa é enviado.
      </Alert>

      <Row className="g-2 mb-3">
        {cartoes.map(([titulo, valor, cor]) => (
          <Col key={titulo} xs={6} md={4} lg={2}>
            <Card body className={`py-2 h-100 border-${cor}`}>
              <div className="small text-muted">{titulo}</div>
              <div className="fs-4 fw-bold">{valor}</div>
            </Card>
          </Col>
        ))}
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={8}>
          <Card>
            <Card.Header>Contas ativas por hora (últimas 48 h)</Card.Header>
            <Card.Body><GraficoHoras horas={grafico.horas} /></Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <Card className="h-100">
            <Card.Header>Contas por versão</Card.Header>
            <Card.Body className="small">
              {grafico.versoes.length === 0 ? <span className="text-muted">Sem dados.</span> : grafico.versoes.map((v) => (
                <div key={`${v.app}-${v.versao}`} className="d-flex justify-content-between border-bottom py-1">
                  <span>{APPS[v.app]} <b>{v.versao}</b></span>
                  <Badge bg="secondary">{v.contas}</Badge>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Header>Contas</Card.Header>
        <Card.Body className="p-0">
          <Table responsive hover size="sm" className="align-middle mb-0">
            <thead>
              <tr><th>Conta</th><th>App</th><th>Versão</th><th>Estado</th><th>Última vez visto</th><th>WhatsApp</th><th>Atenção</th></tr>
            </thead>
            <tbody>
              {contas.map((c) => {
                const [rotulo, cor] = ESTADOS[c.estado];
                return (
                  <tr key={c.id} className={c.ativa ? '' : 'text-muted'}>
                    <td>{c.nome}<div className="small text-muted">{c.email}{c.ativa ? '' : ' · conta inativa'}</div></td>
                    <td>{c.app ? APPS[c.app] : '—'}</td>
                    <td>{c.versao || (c.semVersao ? <span className="text-muted">sem informação</span> : '—')}</td>
                    <td><Badge bg={cor} text={cor === 'warning' ? 'dark' : undefined}>{rotulo}</Badge></td>
                    <td className="text-nowrap">{haQuanto(c.idadeSegundos)}{c.vistoEm ? <div className="small text-muted">{formatarDataHora(c.vistoEm)}</div> : null}</td>
                    <td>{c.waConectado === true ? <Badge bg="success">conectado</Badge> : c.waConectado === false ? <Badge bg="danger">desconectado</Badge> : <span className="text-muted">—</span>}</td>
                    <td>
                      {c.abaixoDoMinimo && <Badge bg="danger" className="me-1">abaixo do mínimo</Badge>}
                      {c.desatualizada && !c.abaixoDoMinimo && <Badge bg="warning" text="dark" className="me-1">desatualizada</Badge>}
                      {c.semVersao && c.ativa && <Badge bg="warning" text="dark" className="me-1">versão antiga</Badge>}
                      {c.ativa && (c.semAcesso || c.estado === 'nunca') && <Badge bg="secondary" className="me-1">sem acesso</Badge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {cfg && (
        <Card className="mb-4">
          <Card.Header>Configuração</Card.Header>
          <Card.Body>
            <Row className="g-3">
              <Col md={4}>
                <Form.Group controlId="cfg-intervalo">
                  <Form.Label>Registrar a atividade de cada conta a cada</Form.Label>
                  <Form.Select value={cfg.intervaloMin} onChange={(e) => setCfg({ ...cfg, intervaloMin: Number(e.target.value) })}>
                    {INTERVALOS.map(([m, r]) => <option key={m} value={m}>{r}</option>)}
                  </Form.Select>
                  <Form.Text muted>Vale na hora, sem mexer nos apps. Menos tempo = "online agora" mais preciso; o custo continua baixo.</Form.Text>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="cfg-dias">
                  <Form.Label>Considerar "sem acesso" depois de (dias)</Form.Label>
                  <Form.Control type="number" min={1} max={90} value={cfg.diasSemAcesso} onChange={(e) => setCfg({ ...cfg, diasSemAcesso: Number(e.target.value) })} />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Check
                  type="switch"
                  id="cfg-resumo-email"
                  className="mt-4"
                  label="E-mail resumo diário (8h)"
                  checked={cfg.resumoEmail !== false}
                  onChange={(e) => setCfg({ ...cfg, resumoEmail: e.target.checked })}
                />
                <Form.Text muted>Apps que pedem atenção e o retrato da escuta nas últimas 24 h.</Form.Text>
              </Col>
              {['chrome', 'desktop'].map((app) => (
                <Col md={6} key={app}>
                  <div className="fw-semibold mb-1">{APPS[app]}</div>
                  <Row className="g-2">
                    <Col>
                      <Form.Label className="small mb-0">Versão mais nova publicada</Form.Label>
                      <Form.Control size="sm" placeholder="ex.: 1.8.0" value={cfg.versaoAtual[app]} onChange={(e) => setCfg({ ...cfg, versaoAtual: { ...cfg.versaoAtual, [app]: e.target.value.trim() } })} />
                    </Col>
                    <Col>
                      <Form.Label className="small mb-0">Versão mínima aceita (opcional)</Form.Label>
                      <Form.Control size="sm" placeholder="vazio = sem mínimo" value={cfg.versaoMinima[app]} onChange={(e) => setCfg({ ...cfg, versaoMinima: { ...cfg.versaoMinima, [app]: e.target.value.trim() } })} />
                    </Col>
                  </Row>
                </Col>
              ))}
            </Row>
            <div className="mt-3">
              <Button onClick={salvar} disabled={salvando || !mudou}>{salvando ? 'Salvando...' : 'Salvar configuração'}</Button>
              <span className="text-muted small ms-3">Ao publicar uma versão nova de um app, atualize "versão mais nova" aqui para ver quem ficou para trás.</span>
            </div>
          </Card.Body>
        </Card>
      )}
    </div>
  );
}
